import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SEED_STEP_GROUP_KEY,
  SEEDDRESS_STEP_GROUP_KEY,
} from 'constants/uiConstants';
import { v4 as uuidv4 } from 'uuid';

import {
  getStepOptionId,
  stepOptionsMatch,
  StepSelectorGroup,
  StepSelectorOption,
} from './stepConfig';
import { StepSelection, toStepSelection } from './stepFactory';

export type CheckedItemsByCategory = Record<string, StepSelectorOption[]>;

const EMPTY_OPTIONS: StepSelectorOption[] = [];

/**
 * Applies a new checked Seed list.
 * - Keeps each seed's pre-generated stepId across replacements (e.g. a variety change).
 * - Gives newly checked seeds a stepId so staged treatments can link to them before Save.
 * - Clears the link on staged treatments whose seed was unchecked.
 */
export const applyCheckedSeeds = (
  previous: CheckedItemsByCategory,
  nextSeeds: StepSelectorOption[]
): CheckedItemsByCategory => {
  const currentSeeds = previous[SEED_STEP_GROUP_KEY] ?? [];
  const seeds = nextSeeds.map((seed) => {
    const stepId =
      currentSeeds.find((current) => stepOptionsMatch(current, seed))?.stepId ??
      seed.stepId ??
      uuidv4();
    return seed.stepId === stepId ? seed : { ...seed, stepId };
  });
  const removedStepIds = new Set(
    currentSeeds
      .map((seed) => seed.stepId)
      .filter(
        (stepId): stepId is string =>
          !!stepId && !seeds.some((seed) => seed.stepId === stepId)
      )
  );
  const next = { ...previous, [SEED_STEP_GROUP_KEY]: seeds };
  const treatments = previous[SEEDDRESS_STEP_GROUP_KEY];
  if (!treatments || removedStepIds.size === 0) return next;
  return {
    ...next,
    [SEEDDRESS_STEP_GROUP_KEY]: treatments.map((treatment) =>
      treatment.linkedSeedStepId &&
      removedStepIds.has(treatment.linkedSeedStepId)
        ? { ...treatment, linkedSeedStepId: null }
        : treatment
    ),
  };
};

/** Every checked item as a selection: seeds, then treatments (so links resolve), then tab order. */
export const buildSaveSelections = (
  checkedItemsByCategory: CheckedItemsByCategory,
  groups: StepSelectorGroup[]
): StepSelection[] => {
  const orderedKeys = [
    SEED_STEP_GROUP_KEY,
    SEEDDRESS_STEP_GROUP_KEY,
    ...groups
      .map((group) => group.key)
      .filter(
        (key) => key !== SEED_STEP_GROUP_KEY && key !== SEEDDRESS_STEP_GROUP_KEY
      ),
  ];
  return orderedKeys.flatMap((categoryKey) => {
    const group = groups.find((candidate) => candidate.key === categoryKey);
    const items = checkedItemsByCategory[categoryKey] ?? [];
    return group
      ? items.map((item) => toStepSelection(item, group.stepType))
      : [];
  });
};

/**
 * Multi-select state for the step picker: the checked items per tab and the preview / lock mode.
 * - Holding Ctrl/Cmd only previews multi-select (`enterMultiSelectMode`); checking or staging
 *   an item locks it (`lockMultiSelect`), and anything checked also counts as locked.
 * - List updates apply to the active tab; Seed updates go through `applyCheckedSeeds`.
 */
const useCheckedSelections = (activeCategoryKey: string) => {
  const [checkedItemsByCategory, setCheckedItemsByCategory] =
    useState<CheckedItemsByCategory>({});
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [isMultiSelectLocked, setIsMultiSelectLocked] = useState(false);
  // Sticky per multi-select session so the hint stays dismissed after a deselect-all.
  const [hasHadSelection, setHasHadSelection] = useState(false);

  const totalCheckedCount = useMemo(
    () =>
      Object.values(checkedItemsByCategory).reduce(
        (sum, items) => sum + items.length,
        0
      ),
    [checkedItemsByCategory]
  );

  useEffect(() => {
    if (totalCheckedCount > 0) {
      setHasHadSelection(true);
    } else if (!isMultiSelectMode) {
      setHasHadSelection(false);
    }
  }, [totalCheckedCount, isMultiSelectMode]);

  const checkedItems =
    checkedItemsByCategory[activeCategoryKey] ?? EMPTY_OPTIONS;
  const checkedSeeds =
    checkedItemsByCategory[SEED_STEP_GROUP_KEY] ?? EMPTY_OPTIONS;
  const checkedSeedTreatments =
    checkedItemsByCategory[SEEDDRESS_STEP_GROUP_KEY] ?? EMPTY_OPTIONS;

  const updateActiveCheckedList = useCallback(
    (update: (currentList: StepSelectorOption[]) => StepSelectorOption[]) => {
      setCheckedItemsByCategory((previous) => {
        const nextList = update(previous[activeCategoryKey] ?? []);
        return activeCategoryKey === SEED_STEP_GROUP_KEY
          ? applyCheckedSeeds(previous, nextList)
          : { ...previous, [activeCategoryKey]: nextList };
      });
    },
    [activeCategoryKey]
  );

  const toggleCheckedItem = useCallback(
    (item: StepSelectorOption) => {
      updateActiveCheckedList((currentList) =>
        currentList.some((existing) => stepOptionsMatch(existing, item))
          ? currentList.filter((existing) => !stepOptionsMatch(existing, item))
          : [...currentList, item]
      );
    },
    [updateActiveCheckedList]
  );

  const upsertCheckedItem = useCallback(
    (item: StepSelectorOption) => {
      updateActiveCheckedList((currentList) => {
        const existingIndex = currentList.findIndex((existing) =>
          stepOptionsMatch(existing, item)
        );
        return existingIndex === -1
          ? [...currentList, item]
          : currentList.map((existing, index) =>
              index === existingIndex ? item : existing
            );
      });
    },
    [updateActiveCheckedList]
  );

  const removeCheckedItem = useCallback(
    (id: string) => {
      updateActiveCheckedList((currentList) =>
        currentList.filter((item) => getStepOptionId(item) !== id)
      );
    },
    [updateActiveCheckedList]
  );

  const isCheckedInActiveCategory = (item: StepSelectorOption) =>
    checkedItems.some((checked) => stepOptionsMatch(checked, item));

  const lockMultiSelect = useCallback(() => {
    setIsMultiSelectMode(true);
    setIsMultiSelectLocked(true);
  }, []);

  const enterMultiSelectMode = useCallback(
    () => setIsMultiSelectMode(true),
    []
  );

  const exitMultiSelectMode = useCallback(
    () => setIsMultiSelectMode(false),
    []
  );

  const resetMultiSelect = useCallback(() => {
    setCheckedItemsByCategory({});
    setIsMultiSelectMode(false);
    setIsMultiSelectLocked(false);
    setHasHadSelection(false);
  }, []);

  return {
    checkedItemsByCategory,
    checkedItems,
    checkedSeeds,
    checkedSeedTreatments,
    totalCheckedCount,
    isMultiSelectMode,
    isMultiSelectLocked,
    isLockedOrHasChecked: isMultiSelectLocked || totalCheckedCount > 0,
    hasHadSelection,
    toggleCheckedItem,
    upsertCheckedItem,
    removeCheckedItem,
    isCheckedInActiveCategory,
    lockMultiSelect,
    enterMultiSelectMode,
    exitMultiSelectMode,
    resetMultiSelect,
  };
};

export type CheckedSelections = ReturnType<typeof useCheckedSelections>;

export default useCheckedSelections;
